'use client';

import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, VStack } from '@chakra-ui/react';
import { TraderDetailHeader } from '@/components/trader/TraderDetailHeader';
import { InfoTabs } from '@/components/trader/InfoTabs';
import { TagsSection } from '@/components/trader/TagsSection';
import { StorySection } from '@/components/trader/StorySection';
import { TraderAccountStats } from '@/components/trader/TraderAccountStats';
import { PortfolioChart } from '@/components/trader/PortfolioChart';
import { TradeStoryOverviewList } from '@/components/trader/TradeStoryOverviewList';
import { LoadingState } from '@/components/common/LoadingState';
import { useCoinIcons } from '@/lib/hooks/useCoinIcons';
import { renderTagColor } from '@/lib/utils/tagColor.utils';
import { useTraderProfile } from '@/lib/hooks/useTraderProfile';
import { mockHomeContentList } from '@/lib/data/mockHomeData';
import { TraderProfile } from '@/components/trader/TraderProfile';
import { PortfolioContent } from '@/components/trader/PortfolioContent';
import { HexString } from '@/lib/config/wagmi';

interface TraderData {
  user: {
    address: string;
    nickname?: string;
  };
  description?: string;
  labels?: string[];
  isFollowed?: boolean;
}

export default function TraderDetailPageMobile() {
  const params = useParams();
  const router = useRouter();
  const searchParams = useSearchParams();
  const address = params.address as string;
  const scrollTo = searchParams.get('scrollTo');

  const { data: traderProfile } = useTraderProfile(address);

  const { fetchIcons } = useCoinIcons();

  const [traderData, setTraderData] = useState<TraderData | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Record<string, number>>({});
  const initTabRef = useRef(false)

  // Store section positions for scrollTo
  const setSectionRef = (sectionId: string, element: HTMLElement | null) => {
    if (element) {
      sectionRefs.current[sectionId] = element.offsetTop - 120;
    }
  };

  useEffect(() => {
    fetchIcons();
  }, []);

  useEffect(() => {
    // Mock trader data - replace with actual API call
    const foundTrader = mockHomeContentList[0];
    setTraderData({ ...foundTrader, user: { ...foundTrader?.user, address } });
  }, [address]);

  // Handle scroll to section when coming from home page
  useEffect(() => {
    if (!initTabRef.current && scrollTo === 'portfolio') {
      initTabRef.current = true;
      setActiveTab(scrollTo);
    }
  }, [scrollTo]);

  const handleTabPress = useCallback((tabId: string) => {
    setActiveTab(tabId);
  }, []);

  if (!traderData) {
    return (
      <Box minH="100vh" bg="black">
        <LoadingState />
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="black" maxW="100vw" overflowX="hidden">
      {/* Fixed Header */}
      <Box position="sticky" top={0} zIndex={10} bg="black">
        <TraderDetailHeader
          traderName={traderProfile?.nickname ?? '- -'}
          traderAddress={address}
        />

        {/* Info Tabs */}
        <InfoTabs
          onTabPress={handleTabPress}
          activeTab={activeTab}
          userAddress={address as HexString}
        />
      </Box>

      {/* Scrollable Content */}
      <Box
        ref={scrollContainerRef}
        height="calc(100vh - 120px)"
        overflowY="auto"
        css={{
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
        }}
      >
        <VStack gap={0} align="stretch">
          {/* Overview Section */}
          {activeTab === 'overview' && (
            <Box>
              <VStack gap={0} align="stretch">
                <VStack gap={4} mb={6}>
                  {/* Tags Section */}
                  <TagsSection
                    tags={renderTagColor(traderProfile?.labels ?? [])}
                  />

                  {/* Story Section */}
                  <StorySection
                    data={{
                      description: traderProfile?.trader ?? '- -',
                      address,
                      nickname: traderProfile?.nickname ?? '- -',
                    }}
                  />
                </VStack>

                {/* Account Stats with Expandable Content */}
                <TraderAccountStats address={address} />

                {/* Chart Section */}
                <PortfolioChart userAddress={address} />

                {/* Trade Story Overview List */}
                <TradeStoryOverviewList address={address} />

                <TraderProfile traderAddress={address} />
              </VStack>
            </Box>
          )}

          {/* Portfolio Section */}
          {activeTab === 'portfolio' && (
            <Box>
              <PortfolioContent hideTitle userAddress={address as `0x${string}`} />
            </Box>
          )}
        </VStack>
      </Box>
    </Box>
  );
}
